import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang, translations, formatEUR } from "@/i18n";
import { CheckoutForm } from "@/components/CheckoutForm";

export const CartSheet = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();
  const { t } = useLang();
  const [showForm, setShowForm] = useState(false);

  const startCheckout = () => {
    if (!items.length) return;
    setOpen(false);
    setShowForm(true);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-sheet" className="w-full sm:max-w-md bg-[#FBF9F6] border-l border-[#E3DCD2] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E3DCD2]">
          <SheetTitle className="font-display text-2xl tracking-tight text-[#1A1514]">{t(translations.cart.title)}</SheetTitle>
          <SheetDescription className="sr-only">{t(translations.cart.note)}</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-3" data-testid="cart-empty">
            <ShoppingBag className="w-10 h-10 text-[#B85C38]" strokeWidth={1.2} />
            <p className="font-display text-lg text-[#1A1514]">{t(translations.cart.empty)}</p>
            <p className="text-sm text-[#5C544F]">{t(translations.cart.emptyCta)}</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 pb-5 border-b border-[#E3DCD2]/70 last:border-0">
                <img src={i.image} alt="" className="w-20 h-20 object-cover rounded-sm bg-[#F2EFEB]" />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-[15px] leading-tight text-[#1A1514]">{i.name}</p>
                  {i.variantLabel && <p className="text-xs text-[#5C544F] mt-1">{i.variantLabel}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-[#E3DCD2] rounded-sm">
                      <button data-testid={`cart-dec-${i.product_id}`} onClick={() => updateQty(i.key, i.quantity - 1)} className="px-2 py-1 hover:bg-[#F2EFEB]"><Minus className="w-3 h-3" /></button>
                      <span className="px-3 text-sm">{i.quantity}</span>
                      <button data-testid={`cart-inc-${i.product_id}`} onClick={() => updateQty(i.key, i.quantity + 1)} className="px-2 py-1 hover:bg-[#F2EFEB]"><Plus className="w-3 h-3" /></button>
                    </div>
                    <span className="text-sm font-medium text-[#1A1514]">{formatEUR(i.unit_price * i.quantity)}</span>
                  </div>
                </div>
                <button data-testid={`cart-remove-${i.product_id}`} onClick={() => removeItem(i.key)} className="text-[#5C544F] hover:text-[#B85C38] self-start">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-[#E3DCD2] px-6 py-5 space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-[#5C544F]">{t(translations.cart.subtotal)}</span>
              <span data-testid="cart-subtotal" className="font-display text-2xl text-[#1A1514]">{formatEUR(subtotal)}</span>
            </div>
            <Button
              data-testid="checkout-button"
              onClick={startCheckout}
              className="w-full bg-[#1A1514] hover:bg-[#3B2A22] text-[#FBF9F6] rounded-sm h-12 text-base"
            >
              {t(translations.cart.checkout)}
            </Button>
            <p className="text-[11px] text-center text-[#5C544F]">{t(translations.cart.note)}</p>
          </div>
        )}
      </SheetContent>

      <CheckoutForm open={showForm} onOpenChange={setShowForm} />
    </Sheet>
  );
};
